import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
  bannerNotificationsQuery,
  dismissBannerNotificationMutation
} from 'Services';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import type { BannerNotification } from 'componentsV2/feature-banner';

const FIVE_MINUTES = 5 * 60 * 1000;

export const useBannerNotifications = () => {
  const { token } = useAppContext();
  const queryClient = useQueryClient();
  const query = bannerNotificationsQuery();

  const { data = [] } = useQuery({
    ...query,
    enabled: !!token, // gate behind auth-ready signal
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1
  });

  // Dismissal is persisted server-side. Optimistically drop the banner from the
  // cache so it disappears instantly; on failure restore it and surface a
  // non-blocking error. Idempotent server-side, so no dedupe needed here.
  const { mutate: dismiss } = useMutation({
    ...dismissBannerNotificationMutation(),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: query.queryKey });
      const previous = queryClient.getQueryData<BannerNotification[]>(
        query.queryKey
      );
      queryClient.setQueryData<BannerNotification[]>(
        query.queryKey,
        (old = []) => old.filter(notification => notification.id !== id)
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous)
        queryClient.setQueryData(query.queryKey, context.previous);
      enqueueSnackbar("Couldn't dismiss the banner. Please try again.", {
        variant: 'error'
      });
    }
  });

  return { notifications: data, dismiss };
};
